import React, { useState, useEffect, useRef } from 'react';

import './../../styles/admin/anime_SKL_IMP.css';

// --- ASSET CONFIGURATION ---
const ASSETS = {
  icons: { tarik: '/assets/icon_tarik.png', panjat: '/assets/icon_panjat.png', sack: '/assets/icon_balap_karung.png', home: '/assets/icon_home.png', },
  tug: { main: '/assets/character.png' },
  sack: { track: '/assets/track.png', blueStand: '/assets/blue_stand.png', blueHop: '/assets/blue_hop.png', redStand: '/assets/red_stand.png', redHop: '/assets/red_hop.png', },
  climb: { pole: '/assets/tiang.png', standLeft: '/assets/standleft.png', climbLeft: '/assets/climbleft.png', standRight: '/assets/standright.png', climbRight: '/assets/climbright.png', }
};

const generateProblem = (operation, difficulty) => {
  let num1, num2, symbol, answer;
  const getRandom = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  const getRange = (diff) => {
    if (diff === 'beginner') return { min: 1, max: 9 };
    if (diff === 'amateur') return { min: 10, max: 99 };
    if (diff === 'pro') return { min: 100, max: 999 };
    return { min: 1, max: 1 };
  };

  const range = getRange(difficulty);

  // Operation specific generation to ensure strict results
  switch (operation) {
    case 'addition':
      symbol = '+';
      // To keep answer within SAME digit count if possible (user said "not triple" for amateur)
      // We limit num1 and num2 so their sum doesn't overflow to next digit count
      num1 = getRandom(range.min, range.max);
      num2 = getRandom(range.min, range.max);

      // If amateur (2-digit), try to keep answer 2-digit (max 99)
      if (difficulty === 'amateur' && num1 + num2 > 99) {
        num1 = getRandom(10, 49);
        num2 = getRandom(10, 50);
      } else if (difficulty === 'pro' && num1 + num2 > 999) {
        num1 = getRandom(100, 499);
        num2 = getRandom(100, 500);
      }
      answer = num1 + num2;
      break;

    case 'subtraction':
      symbol = '-';
      // Ensure answer is also within the same digit range (no single digits for amateur)
      // num1 - num2 >= range.min
      num1 = getRandom(range.min, range.max);
      // num2 must be at least small enough that num1 - num2 >= range.min
      // and num2 itself must be within range.
      let maxNum2 = num1 - range.min;
      if (maxNum2 < range.min) {
        // If num1 is too small (e.g. 15), we need a larger num1
        num1 = getRandom(range.min + 10, range.max);
        maxNum2 = num1 - range.min;
      }
      num2 = getRandom(range.min, Math.min(range.max, maxNum2));
      answer = num1 - num2;
      break;

    case 'multiplication':
      symbol = 'x';
      // Multiplication overflows very fast. 
      // For Amateur (2-digit), 10*10 = 100 (triple). 
      // To stay "not triple", operands must be very small.
      // However, usually people want 2-digit operands. 
      // Given "strict 2+2", I will keep operands 2-digit but answer will be triple.
      // UNLESS the user really wants the answer to be 2-digit too.
      // Let's try to keep answer within range if possible, or prioritize operand digits.
      num1 = getRandom(range.min, range.max);
      num2 = getRandom(range.min, range.max);

      // If we MUST avoid triple digits for amateur:
      if (difficulty === 'amateur' && num1 * num2 > 99) {
        // This would force num1 or num2 to be single digits... 
        // which contradicts "only double digit".
        // So for multiplication, I'll allow triple digit results but keep operands strictly double.
      }
      answer = num1 * num2;
      break;

    case 'division':
      symbol = '÷';
      // num1 / num2 = answer
      // We want num1, num2, and answer to be within range?
      // "2+2 proper" usually means num1 and num2 are 2-digit.
      num2 = getRandom(range.min, range.max);
      // To keep num1 2-digit: multiplier k must be such that num2 * k <= 99
      let maxK = Math.floor(range.max / num2);
      if (maxK < 1) {
        // num2 is too big (e.g. 90), so k can only be 1.
        num1 = num2;
        answer = 1;
      } else {
        let k = getRandom(1, maxK);
        num1 = num2 * k;
        answer = k;
      }
      // Ensure num1 is in range (sometimes num2 * k might be < 10)
      if (num1 < range.min) {
        num2 = getRandom(range.min, Math.floor(range.max / 2));
        num1 = num2 * getRandom(1, Math.floor(range.max / num2));
        answer = num1 / num2;
      }
      break;

    default:
      num1 = getRandom(range.min, range.max);
      num2 = getRandom(range.min, range.max);
      symbol = '+';
      answer = num1 + num2;
  }

  return { questionText: `${num1} ${symbol} ${num2} = ?`, answer: answer };
};



const StarRating = ({ count }) => (
  <div className="star-container">
    {[...Array(count)].map((_, i) => (<svg key={i} className="star-icon" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>))}
  </div>
);

const PlayerPanel = ({ playerId, currentProblem, onCorrectAnswer }) => {
  const [input, setInput] = useState("");
  const [isWrong, setIsWrong] = useState(false);
  const handleNumClick = (num) => { if (input.length < 5) setInput(input + num); setIsWrong(false); };
  const handleClear = () => { setInput(""); setIsWrong(false); };
  const handleSubmit = () => {
    if (parseInt(input) === currentProblem.answer) {
      onCorrectAnswer(); setInput("");
    } else {
      setIsWrong(true); setTimeout(() => setIsWrong(false), 400);
    }
  };
  const headerBg = playerId === 'p1' ? 'bg-blue-dark' : 'bg-red-dark';
  return (
    <div className="glass-panel player-panel">
      <div>
        <div className={`question-box ${headerBg}`}>{currentProblem ? currentProblem.questionText : "Loading..."}</div>
        <div className={`input-box ${isWrong ? 'input-error' : ''}`}>{input}</div>
      </div>
      <div className="numpad-grid">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
          <button key={num} onClick={() => handleNumClick(num)} className="num-btn">{num}</button>
        ))}
        <button onClick={handleClear} className="num-btn num-btn-clear">C</button>
        <button onClick={() => handleNumClick(0)} className="num-btn">0</button>
        <button onClick={handleSubmit} className="num-btn num-btn-go">GO</button>
      </div>
    </div>
  );
};

function App() {
  const [gameState, setGameState] = useState('menu');
  const [settings, setSettings] = useState({ theme: null, operation: null, difficulty: null });
  const [countdown, setCountdown] = useState(3);

  // Gameplay State
  const [p1Score, setP1Score] = useState(0);
  const [p2Score, setP2Score] = useState(0);
  const [tugPosition, setTugPosition] = useState(0);
  const [p1Problem, setP1Problem] = useState(null);
  const [p2Problem, setP2Problem] = useState(null);
  const [winner, setWinner] = useState(null); // FIX 3: Track Who Won

  // Animation States
  const [p1Animating, setP1Animating] = useState(false);
  const [p2Animating, setP2Animating] = useState(false);

  // Constants
  const WIN_SCORE = settings.theme === 'sack-race' ? 10 : 7;
  const TUG_WIN_THRESHOLD = 8; // How many pulls it takes to win tug of war

  const startGame = (customOp, customDiff) => {
    const op = customOp || settings.operation;
    const diff = customDiff || settings.difficulty;

    setGameState('countdown');
    setCountdown(3);
    setP1Score(0); setP2Score(0); setTugPosition(0); setWinner(null);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev === 1) {
          clearInterval(timer); setGameState('playing');
          setP1Problem(generateProblem(op, diff));
          setP2Problem(generateProblem(op, diff));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const triggerAnimation = (setAnimating) => { setAnimating(true); setTimeout(() => setAnimating(false), 500); }

  const handleP1Correct = () => {
    if (gameState !== 'playing') return;
    const newScore = p1Score + 1;
    setP1Score(newScore);

    if (settings.theme === 'tug-of-war') {
      const newTugPos = tugPosition - 1;
      setTugPosition(newTugPos);
      // FIX 3: Check if Blue Pulled Hard Enough to Win
      if (newTugPos <= -TUG_WIN_THRESHOLD) {
        setWinner('Team Blue'); setGameState('finished');
      }
    } else {
      triggerAnimation(setP1Animating);
      if (newScore >= WIN_SCORE) { setWinner('Team Blue'); setGameState('finished'); }
    }
    setP1Problem(generateProblem(settings.operation, settings.difficulty));
  };

  const handleP2Correct = () => {
    if (gameState !== 'playing') return;
    const newScore = p2Score + 1;
    setP2Score(newScore);

    if (settings.theme === 'tug-of-war') {
      const newTugPos = tugPosition + 1;
      setTugPosition(newTugPos);
      // FIX 3: Check if Red Pulled Hard Enough to Win
      if (newTugPos >= TUG_WIN_THRESHOLD) {
        setWinner('Team Red'); setGameState('finished');
      }
    } else {
      triggerAnimation(setP2Animating);
      if (newScore >= WIN_SCORE) { setWinner('Team Red'); setGameState('finished'); }
    }
    setP2Problem(generateProblem(settings.operation, settings.difficulty));
  };

  const renderCenterStage = () => {
    switch (settings.theme) {
      case 'sack-race':
        return (
          <div className="center-stage-panel">
            <h2 className="stage-title">Math Sack Race</h2>
            <div className="sack-container">
              <img src={ASSETS.sack.track} alt="track" className="sack-track" />
              {/* FIX 2: Start precisely on the white tramline at left: 14% and progress nicely */}
              <div className="sack-player sack-p1" style={{ left: `calc(14% + ${(p1Score / WIN_SCORE) * 65}%)`, marginBottom: p1Animating ? '25px' : '0px' }}>
                <img src={p1Animating ? ASSETS.sack.blueHop : ASSETS.sack.blueStand} alt="P1" style={{ width: '100%' }} />
              </div>
              <div className="sack-player sack-p2" style={{ left: `calc(14% + ${(p2Score / WIN_SCORE) * 65}%)`, marginBottom: p2Animating ? '25px' : '0px' }}>
                <img src={p2Animating ? ASSETS.sack.redHop : ASSETS.sack.redStand} alt="P2" style={{ width: '100%' }} />
              </div>
            </div>
            <p className="stage-subtitle">Answer the questions to jump forward! (10 jumps to win)</p>
          </div>
        );
      case 'climbing':
        return (
          <div className="center-stage-panel">
            <h2 className="stage-title" style={{ marginBottom: '0' }}>Mathematical Tree Climbing</h2>
            <div className="climb-container">
              <div className="pole-wrapper" style={{ backgroundImage: `url(${ASSETS.climb.pole})` }}>
                <div className="climber" style={{ bottom: `${(p1Score / WIN_SCORE) * 85}%` }}>
                  <img src={p1Animating ? ASSETS.climb.climbLeft : ASSETS.climb.standLeft} alt="P1 Climb" style={{ width: '100%' }} />
                </div>
              </div>
              <div className="pole-wrapper" style={{ backgroundImage: `url(${ASSETS.climb.pole})` }}>
                <div className="climber" style={{ bottom: `${(p2Score / WIN_SCORE) * 85}%` }}>
                  <img src={p2Animating ? ASSETS.climb.climbRight : ASSETS.climb.standRight} alt="P2 Climb" style={{ width: '100%' }} />
                </div>
              </div>
            </div>
          </div>
        );
      case 'tug-of-war':
        // Max shift limit is 250 pixels
        const shift = Math.max(Math.min(tugPosition * 30, 250), -250);
        return (
          <div className="center-stage-panel">
            <h2 className="stage-title">Math Tug of War</h2>
            <div className="tug-container">
              <div className="tug-sprite" style={{ transform: `translateX(${shift}px)` }}>
                <img src={ASSETS.tug.main} alt="Tug of War" style={{ maxWidth: '100%' }} />
              </div>
              <div className="tug-center-line"></div>
            </div>
            <p className="stage-subtitle">Answer the questions to pull the rope!</p>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="app-wrapper">

      {/* MENU: THEME SELECTION */}
      {gameState === 'menu' && (
        <div className="glass-panel menu-container">
          <h1 className="title-main">Math Games</h1>
          <p className="subtitle">Select Game Theme</p>
          <button className="menu-btn bg-tarik" onClick={() => { setSettings({ ...settings, theme: 'tug-of-war' }); setGameState('operation'); }}>
            <img src={ASSETS.icons.tarik} alt="" className="btn-icon" />
            <div>
              <p className="btn-title">Tug of war</p>
              {/* <p className="btn-subtitle">Inspired by Teacher Salaam</p> */}
            </div>
          </button>
         {/* <button className="menu-btn bg-panjat" onClick={() => { setSettings({ ...settings, theme: 'climbing' }); setGameState('operation'); }}>
            <img src={ASSETS.icons.panjat} alt="" className="btn-icon" />
            <div><p className="btn-title">Climbing slippery pole</p></div>
          </button>
          <button className="menu-btn bg-sack" onClick={() => { setSettings({ ...settings, theme: 'sack-race' }); setGameState('operation'); }}>
            <img src={ASSETS.icons.sack} alt="" className="btn-icon" />
            <div><p className="btn-title">Sack race</p></div>
          </button>*/}
        </div>
      )}

      {/* MENU: OPERATION SELECTION */}
      {gameState === 'operation' && (
        <div className="glass-panel menu-container">
          <h1 className="title-sub">Math {settings.theme?.replace('-', ' ')}</h1>
          <p className="subtitle">Select Operation Type</p>
          {['addition', 'subtraction', 'multiplication', 'division'].map(op => (
            <button key={op} className="menu-btn bg-gradient text-capitalize" style={{ justifyContent: 'center' }} onClick={() => { setSettings({ ...settings, operation: op }); setGameState('difficulty'); }}>
              <p className="btn-title">{op}</p>
            </button>
          ))}

          <button onClick={() => setGameState('menu')} className="back-btn">← Back</button>
        </div>
      )}

      {/* MENU: DIFFICULTY SELECTION */}
      {gameState === 'difficulty' && (
        <div className="glass-panel menu-container">
          <h1 className="title-sub">Select Difficulty Level</h1>
          <p className="subtitle text-capitalize">Mode: {settings.operation}</p>
          <button className="menu-btn bg-beginner" onClick={() => { setSettings({ ...settings, difficulty: 'beginner' }); startGame(settings.operation, 'beginner'); }}>
            <span>Beginner <span className="btn-subtitle" style={{ display: 'block' }}>1 Digit + 1 Digit</span></span>
            <StarRating count={1} />
          </button>
          <button className="menu-btn bg-amateur" onClick={() => { setSettings({ ...settings, difficulty: 'amateur' }); startGame(settings.operation, 'amateur'); }}>
            <span>Amateur <span className="btn-subtitle" style={{ display: 'block' }}>2 Digits + 2 Digits</span></span>
            <StarRating count={2} />
          </button>
          <button className="menu-btn bg-pro" onClick={() => { setSettings({ ...settings, difficulty: 'pro' }); startGame(settings.operation, 'pro'); }}>
            <span>Pro <span className="btn-subtitle" style={{ display: 'block' }}>3 Digits + 3 Digits</span></span>
            <StarRating count={3} />
          </button>


          <button onClick={() => setGameState('operation')} className="back-btn">← Back</button>
        </div>
      )}

      {/* COUNTDOWN */}
      {gameState === 'countdown' && (
        <div className="countdown-screen">
          <h1 className="countdown-text">{countdown}</h1>
        </div>
      )}

      {/* GAMEPLAY */}
      {(gameState === 'playing' || gameState === 'finished') && (
        <div className="game-area">
          <div className="split-screen">
            <div className="player-panel-container">
              <PlayerPanel playerId="p1" currentProblem={p1Problem} onCorrectAnswer={handleP1Correct} />
              {settings.theme !== 'tug-of-war' && <div className="position-tracker text-blue-tracker mt-3">Position: {p1Score}/{WIN_SCORE}</div>}
            </div>

            <div className="center-stage-wrapper relative">
              {gameState === 'finished' && (
                <div className="game-over-overlay">
                  <div className="game-over-box">
                    {/* FIX 3: Announce the Specific Winner */}
                    <h2>{winner} Wins!</h2>
                    <button onClick={() => setGameState('menu')} className="play-again-btn">Play Again</button>
                  </div>
                </div>
              )}
              {renderCenterStage()}
            </div>

            <div className="player-panel-container">
              <PlayerPanel playerId="p2" currentProblem={p2Problem} onCorrectAnswer={handleP2Correct} />
              {settings.theme !== 'tug-of-war' && <div className="position-tracker text-red-tracker mt-3">Position: {p2Score}/{WIN_SCORE}</div>}
            </div>
          </div>

          <div className="footer-controls">
            <button onClick={() => setGameState('menu')} className="home-btn">
              <img src={ASSETS.icons.home} alt="Home" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;